/**
 * UXFU2-03 (fu-r3, WCAG 2.4.3): where keyboard focus goes when an upload-failure dialog closes ("Upload blocked",
 * "Upload failed", "Server unreachable", the offline dialog -- by Ok / Close, Escape, or by itself on `online`).
 *
 * Those dialogs open from the background, while focus sits on <body> (or on a picker control that has since left the
 * page), so the Modal has no opener to go back to and focus fell to <body>: a keyboard or screen-reader user started
 * again at the top of the page. The natural place is the failed row's own Retry; when no row is still failed -- the
 * dialog's Retry (or `online`) has just set them uploading again, or the pick failed before any row existed -- the file
 * picker. Read at close, from the rows' state rather than the DOM: a row that is retrying still shows its old Retry
 * until React re-renders it a moment later.
 */

/** On the row's Retry button (`FileCardComponent`), the row's uuid. */
export const UPLOAD_RETRY_ATTR = 'data-upload-retry';

/** The page's file picker group (`FileUploadComponent`); its first button is "Add Files". */
const PICKER_BUTTONS = '[data-jkey="file-upload-container"] > button:not([disabled])';

interface RetryableRow {
    uuid?: string;
    cancelled?: boolean;
    canRetry?: boolean;
}

function usable(el: Element | null | undefined): el is HTMLElement {
    if (!el || !(el as HTMLElement).isConnected || typeof (el as HTMLElement).focus !== 'function') return false;
    if (el.getAttribute('aria-hidden') === 'true' || el.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    const visible = (el as any).checkVisibility;
    return typeof visible === 'function' ? visible.call(el) !== false : true;
}

function attrValue(value: string): string {
    const css = typeof CSS !== 'undefined' && typeof (CSS as any).escape === 'function' ? (CSS as any).escape : null;
    return css ? css(value) : value.replace(/["\\]/g, '\\$&');
}

/** The failed row's Retry, else the file picker; null when neither is on the page. Never throws. */
export function uploadFailureFocusTarget(rows: Iterable<RetryableRow> | null | undefined, doc?: Document): HTMLElement | null {
    const d = doc ?? (typeof document !== 'undefined' ? document : undefined);
    if (!d) return null;
    try {
        for (const row of rows ?? []) {
            if (!row || row.cancelled || !row.canRetry || !row.uuid) continue;
            const retry = d.querySelector(`[${UPLOAD_RETRY_ATTR}="${attrValue(row.uuid)}"]`);
            if (usable(retry)) return retry;
        }
        for (const button of Array.from(d.querySelectorAll(PICKER_BUTTONS))) {
            if (usable(button)) return button;
        }
    } catch {
        // A selector or a node we cannot read: no target, focus stays where the browser put it.
    }
    return null;
}
